import { useEffect, useState } from "react";
import api, { apiErr, API_BASE } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, FileText, MoreVertical, Pencil, Trash2, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ContractTemplates() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  const load = () => api.get("/contract-templates").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ titre: "", categorie: "", description: "", body: "" }); setAiPrompt(""); setOpen(true); };
  const openEdit = (t) => { setForm({ ...t }); setAiPrompt(""); setOpen(true); };

  const runAI = async (mode) => {
    if (!aiPrompt.trim() && mode === "generate") { toast.error("Décrivez le besoin"); return; }
    setAiBusy(true);
    try {
      const token = localStorage.getItem("omnibat_token");
      const res = await fetch(`${API_BASE}/contracts/ai-generate`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: aiPrompt || "Améliore ce modèle de contrat", mode, current: form.body || "" }),
      });
      if (!res.ok || !res.body) throw new Error("Échec IA");
      const reader = res.body.getReader(); const dec = new TextDecoder();
      let acc = mode === "add_clause" ? `${form.body || ""}\n\n` : "";
      while (true) { const { done, value } = await reader.read(); if (done) break; acc += dec.decode(value, { stream: true }); setForm((p) => ({ ...p, body: acc })); }
      toast.success("Texte généré par l'IA");
    } catch (e) { toast.error("Génération IA échouée"); } finally { setAiBusy(false); }
  };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (form.id) await api.put(`/contract-templates/${form.id}`, form);
      else await api.post("/contract-templates", form);
      toast.success("Modèle enregistré"); setOpen(false); load();
    } catch (err) { toast.error(apiErr(err)); } finally { setSaving(false); }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ce modèle ?")) return; await api.delete(`/contract-templates/${id}`); toast.success("Supprimé"); load(); };

  return (
    <div>
      <PageHeader title="Modèles de contrats" subtitle="Modèles réutilisables · rédaction assistée par IA (Claude Sonnet 5)">
        <Button onClick={openNew} data-testid="add-template" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouveau modèle</Button>
      </PageHeader>

      {items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={FileText} title="Aucun modèle" subtitle="Créez un modèle réutilisable ou générez-en un avec l'IA." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Titre</TableHead><TableHead>Catégorie</TableHead><TableHead>Description</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((t) => (
                <TableRow key={t.id} data-testid={`template-row-${t.id}`} className="hover:bg-slate-50">
                  <TableCell className="font-semibold">{t.titre}</TableCell>
                  <TableCell className="text-slate-600">{t.categorie || "—"}</TableCell>
                  <TableCell className="text-slate-500 text-sm max-w-md truncate">{t.description || "—"}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`template-menu-${t.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(t)}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => remove(t.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{form?.id ? "Modifier le modèle" : "Nouveau modèle de contrat"}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label className="text-xs">Titre</Label><Input value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} required data-testid="template-titre" className="mt-1" /></div>
                <div><Label className="text-xs">Catégorie</Label><Input value={form.categorie} onChange={(e) => setForm({ ...form, categorie: e.target.value })} className="mt-1" placeholder="Prestation, Vente, Sous-traitance…" /></div>
              </div>
              <div><Label className="text-xs">Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>

              <div className="rounded-lg border border-brand-200 bg-brand-50/40 p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-brand-700"><Sparkles className="h-4 w-4" />Assistant IA</div>
                <Textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} rows={2} data-testid="ai-prompt" placeholder="Ex. : Contrat de sous-traitance pour travaux de maçonnerie, durée 3 mois, paiement échelonné…" className="bg-white" />
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" disabled={aiBusy} onClick={() => runAI("generate")} data-testid="ai-generate" className="bg-brand-600 hover:bg-brand-700 text-white">{aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1.5" />Générer</>}</Button>
                  <Button type="button" size="sm" variant="outline" disabled={aiBusy || !form.body} onClick={() => runAI("reformulate")} data-testid="ai-reformulate">Reformuler</Button>
                  <Button type="button" size="sm" variant="outline" disabled={aiBusy} onClick={() => runAI("add_clause")} data-testid="ai-add-clause">Ajouter une clause</Button>
                </div>
              </div>

              <div><Label className="text-xs">Structure du contrat (modifiable)</Label><Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={12} data-testid="template-body" className="mt-1 font-mono text-xs" /></div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button type="submit" disabled={saving} data-testid="template-submit" className="bg-brand-600 hover:bg-brand-700 text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer le modèle"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
